import React, { useEffect, useState, useRef, useCallback } from "react";
import { useForm } from "react-hook-form";
import PageTitle from "@src/components/PageTitle";
import WrapperContainer from "@src/components/WrapperContainer";
import ContentContainer from "@src/components/ContentContainer";
import PageTopSection, {
  TopBarSection,
  TopBarColumn,
} from "@src/components/PageTopSection/PageTopSection";
import FormToggleSwitch from "@src/components/FormToggleSwitch";
import ConversationSidebar from "./ConversationSidebar";
import ChatView from "./ChatView";
import {
  TabsContainer,
  TabButton,
  TabBadge,
  TopBarControls,
  ConversationSourcePill,
  ConversationSourceText,
  ConversationMetaPills,
  FIGMA_MESSAGES_HEADER_BG,
} from "./MessagesPage.styles";
import { theme } from "@src/theme";
import { MessageSquare } from "lucide-react";
import { useAuthCurator } from "@src/hooks/useAuth";
import { useAppDispatch } from "@app-redux/hooks";
import { updateAuthCurator } from "@src/redux/slices/authSlice";
import { ICuratorConversation } from "@src/types/curator";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import {
  updateCuratorSettings,
  getCuratorSettings,
  getCuratorConversations,
  markConversationRead,
} from "@src/apis/curatorConversationApis";
import { useNotification } from "@src/context/NotificationContext";
import { useCuratorConversationsContext } from "@src/context/CuratorConversationsContext";
import { CONVERSATIONS_POLL_INTERVAL_MS } from "@src/constants/ivsChat";
import {
  countConversationsByStatus,
  countUnreadConversationsByStatus,
  filterConversationsByTab,
  getConversationId,
  getConversationPlaylistSource,
  getConversationStatus,
  getConversationSubject,
  type MessagesTab,
} from "./conversationUtils";

interface TogglesForm {
  allowMessages: boolean;
}

const MessagesPage = (): React.JSX.Element => {
  const curator = useAuthCurator();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<MessagesTab>("inbox");
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);

  const { addNotification } = useNotification();
  const { control, reset, getValues, watch } = useForm<TogglesForm>({
    defaultValues: {
      allowMessages: true,
    },
  });

  const allowMessages = watch("allowMessages");

  const { isEventsConnected, refreshConversations } =
    useCuratorConversationsContext();

  const { data: conversations = [], isLoading } = useQuery<
    ICuratorConversation[]
  >({
    queryKey: ["conversations"],
    queryFn: () => getCuratorConversations(),
    enabled: !!curator?.documentId,
    refetchInterval: isEventsConnected ? false : CONVERSATIONS_POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

  // Unread-driven, not a total: the badge means "new requests you have not
  // looked at". Opening a request (even without accepting it) marks it read and
  // clears the badge, matching the mobile app.
  const requestCount = countUnreadConversationsByStatus(conversations, [
    "requested",
  ]);
  const blockedCount = countConversationsByStatus(conversations, ["blocked"]);
  const endedCount = countConversationsByStatus(conversations, ["ended"]);

  const [hasInitialized, setHasInitialized] = useState(false);

  // Immediate value from the persisted login snapshot (avoids a flash before the
  // authoritative fetch below resolves).
  useEffect(() => {
    if (curator && !hasInitialized) {
      reset({
        allowMessages: curator.receiveMessages ?? true,
      });
      setHasInitialized(true);
    }
  }, [curator, reset, hasInitialized]);

  // Source of truth: reconcile the toggle with the current backend value on
  // load. redux-persist only refreshes the curator snapshot at login, so
  // without this the toggle drifts from Strapi — reverting on refresh and
  // ignoring changes made directly in Strapi.
  const { data: liveSettings } = useQuery({
    queryKey: ["curator-settings", curator?.documentId],
    queryFn: () => getCuratorSettings(curator!.documentId),
    enabled: !!curator?.documentId,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!liveSettings) return;
    const value = liveSettings.receiveMessages ?? true;
    reset({ allowMessages: value });
    dispatch(updateAuthCurator({ receiveMessages: value }));
  }, [liveSettings, reset, dispatch]);

  const markConversationAsRead = useCallback(
    async (conversationId: string) => {
      try {
        await markConversationRead(conversationId);
        queryClient.invalidateQueries({ queryKey: ["conversations"] });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to mark conversation as read", error);
      }
    },
    [queryClient]
  );

  const handleSelectConversation = useCallback(
    (conversationId: string) => {
      setSelectedConversationId((current) => {
        const nextId = current === conversationId ? null : conversationId;

        if (current && current !== nextId) {
          void markConversationAsRead(current);
        }

        return nextId;
      });
    },
    [markConversationAsRead]
  );

  useEffect(() => {
    if (!selectedConversationId) return;
    const visible = filterConversationsByTab(conversations, activeTab);
    const stillVisible = visible.some(
      (conversation) =>
        getConversationId(conversation) === selectedConversationId
    );
    if (!stillVisible) {
      void markConversationAsRead(selectedConversationId);
      setSelectedConversationId(null);
    }
  }, [
    activeTab,
    conversations,
    markConversationAsRead,
    selectedConversationId,
  ]);

  const updateSettingsMutation = useMutation({
    mutationFn: (data: Partial<TogglesForm>) => {
      if (!curator?.documentId) return Promise.reject("No curator");
      return updateCuratorSettings(curator.documentId, {
        receiveMessages: data.allowMessages,
      });
    },
    onSuccess: (_data, variables) => {
      if (typeof variables.allowMessages === "boolean") {
        // Reflect the saved value in the persisted curator + this page's cache
        // so it survives a refresh instead of reverting to the login snapshot.
        dispatch(
          updateAuthCurator({ receiveMessages: variables.allowMessages })
        );
        queryClient.setQueryData(
          ["curator-settings", curator?.documentId],
          (
            old: { receiveMessages?: boolean; aiModeration?: boolean } = {}
          ) => ({
            ...old,
            receiveMessages: variables.allowMessages,
          })
        );
      }
      queryClient.invalidateQueries({ queryKey: ["curators"] });
      addNotification("Settings updated", "success");
    },
    onError: () => {
      addNotification("Failed to update settings", "error");
    },
  });

  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const handleToggleChange = (field: keyof TogglesForm, value: boolean) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    const currentValues = getValues();
    const newValues = { ...currentValues, [field]: value };

    debounceTimer.current = setTimeout(() => {
      updateSettingsMutation.mutate(newValues);
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const handleConversationAccepted = () => {
    setActiveTab("inbox");
  };

  const selectedConversation = conversations.find(
    (conversation: ICuratorConversation) =>
      getConversationId(conversation) === selectedConversationId
  );
  const conversationPlaylistSource = selectedConversation
    ? getConversationPlaylistSource(selectedConversation)
    : null;
  const conversationSubject = selectedConversation
    ? getConversationSubject(selectedConversation)
    : null;

  return (
    <WrapperContainer noScroll>
      <PageTopSection backgroundColor={FIGMA_MESSAGES_HEADER_BG}>
        <TopBarSection columnCount={2} style={{ overflow: "visible" }}>
          <TopBarColumn>
            <PageTitle>Messages</PageTitle>
            <TabsContainer>
              <TabButton
                $active={activeTab === "inbox"}
                onClick={() => setActiveTab("inbox")}
              >
                Inbox
              </TabButton>
              <TabButton
                $active={activeTab === "requests"}
                onClick={() => setActiveTab("requests")}
              >
                Requests
                {requestCount > 0 && <TabBadge>{requestCount}</TabBadge>}
              </TabButton>
              <TabButton
                $active={activeTab === "blocked"}
                onClick={() => setActiveTab("blocked")}
              >
                Blocked
                {blockedCount > 0 && <TabBadge>{blockedCount}</TabBadge>}
              </TabButton>
              <TabButton
                $active={activeTab === "ended"}
                onClick={() => setActiveTab("ended")}
              >
                Ended chats
                {endedCount > 0 && <TabBadge>{endedCount}</TabBadge>}
              </TabButton>
            </TabsContainer>
          </TopBarColumn>
          <TopBarColumn
            style={{
              alignItems: "flex-end",
              overflow: "visible",
              paddingRight: 4,
              boxSizing: "border-box",
            }}
          >
            <TopBarControls>
              <FormToggleSwitch
                name="allowMessages"
                label="Allow Messages"
                control={control}
                variant="circular"
                onChange={(checked) =>
                  handleToggleChange("allowMessages", checked)
                }
              />
              {selectedConversation &&
                (activeTab === "inbox" || activeTab === "requests") &&
                (conversationPlaylistSource || conversationSubject) && (
                  <ConversationMetaPills>
                    {conversationPlaylistSource && (
                      <ConversationSourcePill
                        title={`From: ${conversationPlaylistSource}`}
                      >
                        <MessageSquare
                          size={14}
                          fill={theme.colors.black}
                          color={theme.colors.black}
                          strokeWidth={1.5}
                        />
                        <ConversationSourceText>
                          From: {conversationPlaylistSource}
                        </ConversationSourceText>
                      </ConversationSourcePill>
                    )}
                    {conversationSubject && (
                      <ConversationSourcePill
                        title={`Subject: ${conversationSubject}`}
                      >
                        <ConversationSourceText>
                          Subject: {conversationSubject}
                        </ConversationSourceText>
                      </ConversationSourcePill>
                    )}
                  </ConversationMetaPills>
                )}
            </TopBarControls>
          </TopBarColumn>
        </TopBarSection>
      </PageTopSection>

      <ContentContainer
        contentContainerStyles={{
          borderTop: `1px solid ${theme.colors.darkGray}`,
          flexDirection: "row",
          minHeight: 0,
        }}
      >
        <ConversationSidebar
          activeTab={activeTab}
          selectedConversationId={selectedConversationId}
          onSelectConversation={handleSelectConversation}
          conversations={conversations}
          isLoading={isLoading}
        />
        <ChatView
          conversationId={selectedConversationId}
          conversation={selectedConversation}
          conversationStatus={
            selectedConversation
              ? getConversationStatus(selectedConversation)
              : undefined
          }
          curatorId={curator?.id}
          curatorDocumentId={curator?.documentId}
          curatorName={curator?.name}
          activeTab={activeTab}
          allowMessages={allowMessages}
          onConversationAccepted={handleConversationAccepted}
          onConversationRead={markConversationAsRead}
          onLiveMessage={refreshConversations}
        />
      </ContentContainer>
    </WrapperContainer>
  );
};

export default MessagesPage;
